import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { Stack, Card } from '@repletoryreach/sdk-ui';
import { PageHeader, BreadcrumbTrail } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import { SETTINGS_NAV, activeSettingsLink, settingsLinkShown } from '../nav';
import { useIsOrgAdmin } from '../../../app/components/RoleGate';
import { useSmsStatus } from '../../../lib/sms';
import { useEmailStatus } from '../../../lib/email';
import './settings-layout.css';
import { HelpButton } from '../../../app/guides/HelpButton';

export default function SettingsLayout() {
  const t = useT();
  const isAdmin = useIsOrgAdmin();
  const { available: smsAvailable } = useSmsStatus();
  const { available: emailAvailable } = useEmailStatus();
  const shown = (item: Parameters<typeof settingsLinkShown>[0]) => settingsLinkShown(item, isAdmin, { sms: smsAvailable, email: emailAvailable });
  const { pathname } = useLocation();
  const active = activeSettingsLink(pathname);
  const onHub = pathname === '/settings';

  return (
    <Stack gap="lg">
      <PageHeader
        /*
         * B4 items 3 and 4: the header always said "Settings" with a generic
         * description, and each sub-page opened with an `<h4>` inside a card,
         * so the page's own name was never the heading and the user was told
         * where they were only by a highlighted nav item.
         */
        title={active && !onHub ? t(active.labelKey) : t('nav.section.settings')}
        description={active && !onHub ? t(active.descriptionKey) : t('settings.layout.description')}
        withDivider={false}
        breadcrumbs={active && !onHub
          ? (
            <BreadcrumbTrail
              items={[
                { label: t('nav.section.settings'), href: '/settings' },
                { label: t(active.labelKey) }
              ]}
              renderLink={(href, label) => <Link to={href}>{label}</Link>}
            />
          )
          : undefined}
        actions={<HelpButton guide="account" />}
      />

      <div className="rr-settings">
        {/*
          B4 items 9 and 10: eleven links with no landmark, and group headings
          that were styled `<Box>`s associated with nothing, so a screen-reader
          user heard eleven ungrouped links.
        */}
        <nav className="rr-settings__nav" aria-label={t('nav.section.settings')}>
          <Card withBorder className="rr-settings__nav-card" data-guide="settings-nav">
            <Stack gap="md">
              {SETTINGS_NAV.filter((section) => section.items.some(shown)).map((section) => (
                <div key={section.labelKey} role="group" aria-labelledby={`settings-group-${section.labelKey}`}>
                  <div className="rr-settings__group" id={`settings-group-${section.labelKey}`}>
                    {t(section.labelKey)}
                  </div>
                  {/* Agents do not see owner-and-admin settings (2 October 2026). */}
                  {section.items.filter(shown).map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.end}
                      className={({ isActive }) => `rr-settings__link${isActive ? ' is-active' : ''}`}
                    >
                      {t(item.labelKey)}
                    </NavLink>
                  ))}
                </div>
              ))}
            </Stack>
          </Card>
        </nav>

        <div className="rr-settings__body">
          <Outlet />
        </div>
      </div>
    </Stack>
  );
}
